import { useState, useEffect, useCallback, useRef } from 'react';
import { useDatabase } from '../context/DatabaseContext';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFocusEffect } from '@react-navigation/native';

/**
 * Custom hooks for database operations in JobEng App
 * Provides reusable database functionality for all screens
 * ABSOLUTE FINAL COMPLETE VERSION WITH ALL HOOKS INCLUDING useSubscriptions
 */

/**
 * Hook for managing subscriptions - MISSING HOOK ADDED
 */
export function useSubscriptions(userId) {
  const {
    subscriptions,
    loading,
    errors,
    loadSubscriptions,
    createSubscription,
    updateSubscription,
    cancelSubscription,
    clearError,
  } = useDatabase();

  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Load subscriptions on mount
  useEffect(() => {
    if (userId) {
      loadSubscriptions && loadSubscriptions(userId);
    }
  }, [userId, loadSubscriptions]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      clearError && clearError('subscriptions');
    };
  }, [clearError]);

  const handleCreateSubscription = useCallback(async (subscriptionData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = createSubscription ? await createSubscription(subscriptionData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [createSubscription]);

  const handleUpdateSubscription = useCallback(async (subscriptionId, subscriptionData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = updateSubscription ? await updateSubscription(subscriptionId, subscriptionData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [updateSubscription]);

  const handleCancelSubscription = useCallback(async (subscriptionId) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = cancelSubscription ? await cancelSubscription(subscriptionId) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [cancelSubscription]);

  const refreshSubscriptions = useCallback(() => {
    if (userId && loadSubscriptions) {
      return loadSubscriptions(userId);
    }
  }, [userId, loadSubscriptions]);

  // Get active subscriptions
  const getActiveSubscriptions = useCallback(() => {
    return (subscriptions || []).filter(subscription => subscription.status === 'active');
  }, [subscriptions]);

  // Get subscriptions by plan
  const getSubscriptionsByPlan = useCallback((planType) => {
    return (subscriptions || []).filter(subscription => subscription.planType === planType);
  }, [subscriptions]);

  // Get current subscription
  const getCurrentSubscription = useCallback(() => {
    const activeSubscriptions = getActiveSubscriptions();
    return activeSubscriptions.length > 0 ? activeSubscriptions[0] : null;
  }, [getActiveSubscriptions]);

  // Check if user has active subscription
  const hasActiveSubscription = useCallback(() => {
    return getActiveSubscriptions().length > 0;
  }, [getActiveSubscriptions]);

  // Get subscription by status
  const getSubscriptionsByStatus = useCallback((status) => {
    return (subscriptions || []).filter(subscription => subscription.status === status);
  }, [subscriptions]);

  // Calculate total subscription cost
  const getTotalSubscriptionCost = useCallback(() => {
    return (subscriptions || []).reduce((total, subscription) => {
      if (subscription.status === 'active') {
        return total + (subscription.price || 0);
      }
      return total;
    }, 0);
  }, [subscriptions]);

  // Get subscription plans
  const getAvailablePlans = useCallback(() => {
    return [
      {
        id: 'team',
        name: 'Team',
        price: 29,
        currency: 'USD',
        interval: 'month',
        features: [
          'Up to 10 team members',
          'Basic project management',
          'Time tracking',
          'Email support',
          '5GB storage'
        ],
        recommended: false,
      },
      {
        id: 'business',
        name: 'Business',
        price: 79,
        currency: 'USD',
        interval: 'month',
        features: [
          'Up to 50 team members',
          'Advanced project management',
          'Time tracking & reporting',
          'Priority support',
          '50GB storage',
          'Custom integrations'
        ],
        recommended: true,
      },
      {
        id: 'enterprise',
        name: 'Enterprise',
        price: 199,
        currency: 'USD',
        interval: 'month',
        features: [
          'Unlimited team members',
          'Full project suite',
          'Advanced analytics',
          '24/7 dedicated support',
          'Unlimited storage',
          'Custom development',
          'On-premise deployment'
        ],
        recommended: false,
      }
    ];
  }, []);

  // Upgrade subscription
  const upgradeSubscription = useCallback(async (newPlanId) => {
    const currentSubscription = getCurrentSubscription();
    if (!currentSubscription) {
      return { success: false, error: 'No active subscription found' };
    }

    const subscriptionData = {
      ...currentSubscription,
      planType: newPlanId,
      updatedAt: new Date().toISOString(),
    };

    return handleUpdateSubscription(currentSubscription.id, subscriptionData);
  }, [getCurrentSubscription, handleUpdateSubscription]);

  // Downgrade subscription
  const downgradeSubscription = useCallback(async (newPlanId) => {
    const currentSubscription = getCurrentSubscription();
    if (!currentSubscription) {
      return { success: false, error: 'No active subscription found' };
    }

    const subscriptionData = {
      ...currentSubscription,
      planType: newPlanId,
      updatedAt: new Date().toISOString(),
    };

    return handleUpdateSubscription(currentSubscription.id, subscriptionData);
  }, [getCurrentSubscription, handleUpdateSubscription]);

  // Get subscription features
  const getSubscriptionFeatures = useCallback((planType) => {
    const plans = getAvailablePlans();
    const plan = plans.find(p => p.id === planType);
    return plan ? plan.features : [];
  }, [getAvailablePlans]);

  // Check if feature is available
  const hasFeature = useCallback((featureName) => {
    const currentSubscription = getCurrentSubscription();
    if (!currentSubscription) return false;

    const features = getSubscriptionFeatures(currentSubscription.planType);
    return features.some(feature => feature.toLowerCase().includes(featureName.toLowerCase()));
  }, [getCurrentSubscription, getSubscriptionFeatures]);

  return {
    subscriptions: subscriptions || [],
    loading: (loading && loading.subscriptions) || localLoading,
    error: (errors && errors.subscriptions) || localError,
    createSubscription: handleCreateSubscription,
    updateSubscription: handleUpdateSubscription,
    cancelSubscription: handleCancelSubscription,
    refreshSubscriptions,
    getActiveSubscriptions,
    getSubscriptionsByPlan,
    getCurrentSubscription,
    hasActiveSubscription,
    getSubscriptionsByStatus,
    getTotalSubscriptionCost,
    getAvailablePlans,
    upgradeSubscription,
    downgradeSubscription,
    getSubscriptionFeatures,
    hasFeature,
  };
}

/**
 * Hook for managing companies
 */
export function useCompanies() {
  const {
    companies,
    loading,
    errors,
    loadCompanies,
    createCompany,
    updateCompany,
    deleteCompany,
    clearError,
  } = useDatabase();

  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Load companies on mount
  useEffect(() => {
    loadCompanies && loadCompanies();
  }, [loadCompanies]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      clearError && clearError('companies');
    };
  }, [clearError]);

  const handleCreateCompany = useCallback(async (companyData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = createCompany ? await createCompany(companyData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [createCompany]);

  const handleUpdateCompany = useCallback(async (companyId, companyData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = updateCompany ? await updateCompany(companyId, companyData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [updateCompany]);

  const handleDeleteCompany = useCallback(async (companyId) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = deleteCompany ? await deleteCompany(companyId) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [deleteCompany]);

  const refreshCompanies = useCallback(() => {
    if (loadCompanies) {
      return loadCompanies();
    }
  }, [loadCompanies]);

  // Get companies by industry
  const getCompaniesByIndustry = useCallback((industry) => {
    return (companies || []).filter(company => company.industry === industry);
  }, [companies]);

  // Get companies by size
  const getCompaniesBySize = useCallback((size) => {
    return (companies || []).filter(company => company.size === size);
  }, [companies]);

  // Get active companies
  const getActiveCompanies = useCallback(() => {
    return (companies || []).filter(company => company.status === 'active');
  }, [companies]);

  // Search companies by name
  const searchCompanies = useCallback((query) => {
    if (!query) return companies || [];
    const lowercaseQuery = query.toLowerCase();
    return (companies || []).filter(company => 
      company.name?.toLowerCase().includes(lowercaseQuery) ||
      company.description?.toLowerCase().includes(lowercaseQuery)
    );
  }, [companies]);

  // Get company by code
  const getCompanyByCode = useCallback((code) => {
    return (companies || []).find(company => company.code === code);
  }, [companies]);

  // Generate unique company code
  const generateCompanyCode = useCallback(() => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    // Check if code already exists
    const existingCompany = getCompanyByCode(code);
    if (existingCompany) {
      return generateCompanyCode(); // Recursively generate new code
    }
    
    return code;
  }, [getCompanyByCode]);

  // Validate company code
  const validateCompanyCode = useCallback((code) => {
    if (!code || code.length !== 8) {
      return { isValid: false, error: 'Company code must be 8 characters long' };
    }
    
    const company = getCompanyByCode(code);
    if (!company) {
      return { isValid: false, error: 'Company code not found' };
    }
    
    if (company.status !== 'active') {
      return { isValid: false, error: 'Company is not active' };
    }
    
    return { isValid: true, company };
  }, [getCompanyByCode]);

  return {
    companies: companies || [],
    loading: (loading && loading.companies) || localLoading,
    error: (errors && errors.companies) || localError,
    createCompany: handleCreateCompany,
    updateCompany: handleUpdateCompany,
    deleteCompany: handleDeleteCompany,
    refreshCompanies,
    getCompaniesByIndustry,
    getCompaniesBySize,
    getActiveCompanies,
    searchCompanies,
    getCompanyByCode,
    generateCompanyCode,
    validateCompanyCode,
  };
}

/**
 * Hook for managing users
 */
export function useUsers() {
  const {
    users,
    loading,
    errors,
    loadUsers,
    createUser,
    updateUser,
    deleteUser,
    clearError,
  } = useDatabase();

  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Load users on mount
  useEffect(() => {
    loadUsers && loadUsers();
  }, [loadUsers]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      clearError && clearError('users');
    };
  }, [clearError]);

  const handleCreateUser = useCallback(async (userData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = createUser ? await createUser(userData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [createUser]);

  const handleUpdateUser = useCallback(async (userId, userData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = updateUser ? await updateUser(userId, userData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [updateUser]);

  const handleDeleteUser = useCallback(async (userId) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = deleteUser ? await deleteUser(userId) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [deleteUser]);

  const refreshUsers = useCallback(() => {
    if (loadUsers) {
      return loadUsers();
    }
  }, [loadUsers]);

  // Get users by type
  const getUsersByType = useCallback((userType) => {
    return (users || []).filter(user => user.userType === userType);
  }, [users]);

  // Get active users
  const getActiveUsers = useCallback(() => {
    return (users || []).filter(user => user.status === 'active');
  }, [users]);

  // Get users by company
  const getUsersByCompany = useCallback((companyId) => {
    return (users || []).filter(user => user.companyId === companyId);
  }, [users]);

  // Search users by name or email
  const searchUsers = useCallback((query) => {
    if (!query) return users || [];
    const lowercaseQuery = query.toLowerCase();
    return (users || []).filter(user => 
      user.name?.toLowerCase().includes(lowercaseQuery) ||
      user.email?.toLowerCase().includes(lowercaseQuery)
    );
  }, [users]);

  return {
    users: users || [],
    loading: (loading && loading.users) || localLoading,
    error: (errors && errors.users) || localError,
    createUser: handleCreateUser,
    updateUser: handleUpdateUser,
    deleteUser: handleDeleteUser,
    refreshUsers,
    getUsersByType,
    getActiveUsers,
    getUsersByCompany,
    searchUsers,
  };
}

/**
 * Hook for managing reports
 */
export function useReports(userId) {
  const {
    reports,
    loading,
    errors,
    loadReports,
    generateReport,
    deleteReport,
    clearError,
  } = useDatabase();

  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Load reports on mount
  useEffect(() => {
    if (userId) {
      loadReports && loadReports(userId);
    }
  }, [userId, loadReports]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      clearError && clearError('reports');
    };
  }, [clearError]);

  const handleGenerateReport = useCallback(async (reportData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = generateReport ? await generateReport(reportData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [generateReport]);

  const handleDeleteReport = useCallback(async (reportId) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = deleteReport ? await deleteReport(reportId) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [deleteReport]);

  const refreshReports = useCallback(() => {
    if (userId && loadReports) {
      return loadReports(userId);
    }
  }, [userId, loadReports]);

  // Get reports by type
  const getReportsByType = useCallback((type) => {
    return (reports || []).filter(report => report.type === type);
  }, [reports]);

  // Get reports by date range
  const getReportsByDateRange = useCallback((startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    return (reports || []).filter(report => {
      const reportDate = new Date(report.createdAt);
      return reportDate >= start && reportDate <= end;
    });
  }, [reports]);

  // Get recent reports
  const getRecentReports = useCallback((limit = 10) => {
    return (reports || [])
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, limit);
  }, [reports]);

  // Generate project performance report
  const generateProjectPerformanceReport = useCallback(async (projectId, dateRange) => {
    const reportData = {
      type: 'project_performance',
      projectId,
      dateRange,
      userId,
      title: 'Project Performance Report',
      description: 'Comprehensive project performance analysis',
    };
    return handleGenerateReport(reportData);
  }, [userId, handleGenerateReport]);

  // Generate team productivity report
  const generateTeamProductivityReport = useCallback(async (teamId, dateRange) => {
    const reportData = {
      type: 'team_productivity',
      teamId,
      dateRange,
      userId,
      title: 'Team Productivity Report',
      description: 'Team performance and productivity analysis',
    };
    return handleGenerateReport(reportData);
  }, [userId, handleGenerateReport]);

  // Generate financial report
  const generateFinancialReport = useCallback(async (dateRange, reportType = 'summary') => {
    const reportData = {
      type: 'financial',
      subType: reportType,
      dateRange,
      userId,
      title: 'Financial Report',
      description: 'Financial performance and budget analysis',
    };
    return handleGenerateReport(reportData);
  }, [userId, handleGenerateReport]);

  // Generate time tracking report
  const generateTimeTrackingReport = useCallback(async (employeeId, dateRange) => {
    const reportData = {
      type: 'time_tracking',
      employeeId,
      dateRange,
      userId,
      title: 'Time Tracking Report',
      description: 'Employee time tracking and hours analysis',
    };
    return handleGenerateReport(reportData);
  }, [userId, handleGenerateReport]);

  return {
    reports: reports || [],
    loading: (loading && loading.reports) || localLoading,
    error: (errors && errors.reports) || localError,
    generateReport: handleGenerateReport,
    deleteReport: handleDeleteReport,
    refreshReports,
    getReportsByType,
    getReportsByDateRange,
    getRecentReports,
    generateProjectPerformanceReport,
    generateTeamProductivityReport,
    generateFinancialReport,
    generateTimeTrackingReport,
  };
}

/**
 * Hook for managing contracts
 */
export function useContracts(userId) {
  const {
    contracts,
    loading,
    errors,
    loadContracts,
    createContract,
    updateContract,
    deleteContract,
    clearError,
  } = useDatabase();

  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Load contracts on mount
  useEffect(() => {
    if (userId) {
      loadContracts && loadContracts(userId);
    }
  }, [userId, loadContracts]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      clearError && clearError('contracts');
    };
  }, [clearError]);

  const handleCreateContract = useCallback(async (contractData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = createContract ? await createContract(contractData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [createContract]);

  const handleUpdateContract = useCallback(async (contractId, contractData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = updateContract ? await updateContract(contractId, contractData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [updateContract]);

  const handleDeleteContract = useCallback(async (contractId) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = deleteContract ? await deleteContract(contractId) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [deleteContract]);

  const refreshContracts = useCallback(() => {
    if (userId && loadContracts) {
      return loadContracts(userId);
    }
  }, [userId, loadContracts]);

  // Get active contracts
  const getActiveContracts = useCallback(() => {
    return (contracts || []).filter(contract => contract.status === 'active');
  }, [contracts]);

  // Get contracts by status
  const getContractsByStatus = useCallback((status) => {
    return (contracts || []).filter(contract => contract.status === status);
  }, [contracts]);

  // Get contracts by client
  const getContractsByClient = useCallback((clientId) => {
    return (contracts || []).filter(contract => contract.clientId === clientId);
  }, [contracts]);

  // Calculate total contract value
  const getTotalContractValue = useCallback(() => {
    return (contracts || []).reduce((total, contract) => total + (contract.value || 0), 0);
  }, [contracts]);

  // Get contracts ending soon (within 30 days)
  const getContractsEndingSoon = useCallback(() => {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    
    return (contracts || []).filter(contract => {
      if (!contract.endDate || contract.status !== 'active') return false;
      return new Date(contract.endDate) <= thirtyDaysFromNow;
    });
  }, [contracts]);

  // Get contract performance metrics
  const getContractPerformance = useCallback(() => {
    const activeContracts = getActiveContracts();
    if (activeContracts.length === 0) return { completion: 0, onTime: 0, budget: 0 };
    
    const totalCompletion = activeContracts.reduce((sum, contract) => sum + (contract.completion || 0), 0);
    const onTimeContracts = activeContracts.filter(contract => contract.onSchedule).length;
    const onBudgetContracts = activeContracts.filter(contract => contract.onBudget).length;
    
    return {
      completion: Math.round(totalCompletion / activeContracts.length),
      onTime: Math.round((onTimeContracts / activeContracts.length) * 100),
      budget: Math.round((onBudgetContracts / activeContracts.length) * 100),
    };
  }, [getActiveContracts]);

  return {
    contracts: contracts || [],
    loading: (loading && loading.contracts) || localLoading,
    error: (errors && errors.contracts) || localError,
    createContract: handleCreateContract,
    updateContract: handleUpdateContract,
    deleteContract: handleDeleteContract,
    refreshContracts,
    getActiveContracts,
    getContractsByStatus,
    getContractsByClient,
    getTotalContractValue,
    getContractsEndingSoon,
    getContractPerformance,
  };
}

/**
 * Hook for managing projects
 */
export function useProjects(userId) {
  const {
    projects,
    loading,
    errors,
    loadProjects,
    createProject,
    updateProject,
    deleteProject,
    clearError,
  } = useDatabase();

  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Load projects on mount
  useEffect(() => {
    if (userId) {
      loadProjects && loadProjects(userId);
    }
  }, [userId, loadProjects]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      clearError && clearError('projects');
    };
  }, [clearError]);

  const handleCreateProject = useCallback(async (projectData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = createProject ? await createProject(projectData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [createProject]);

  const handleUpdateProject = useCallback(async (projectId, projectData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = updateProject ? await updateProject(projectId, projectData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [updateProject]);

  const handleDeleteProject = useCallback(async (projectId) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = deleteProject ? await deleteProject(projectId) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [deleteProject]);

  const refreshProjects = useCallback(() => {
    if (userId && loadProjects) {
      return loadProjects(userId);
    }
  }, [userId, loadProjects]);

  return {
    projects: projects || [],
    loading: (loading && loading.projects) || localLoading,
    error: (errors && errors.projects) || localError,
    createProject: handleCreateProject,
    updateProject: handleUpdateProject,
    deleteProject: handleDeleteProject,
    refreshProjects,
  };
}

/**
 * Hook for managing team members
 */
export function useTeamMembers(userId) {
  const {
    teamMembers,
    loading,
    errors,
    loadTeamMembers,
    createTeamMember,
    updateTeamMember,
    deleteTeamMember,
    clearError,
  } = useDatabase();

  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Load team members on mount
  useEffect(() => {
    if (userId) {
      loadTeamMembers && loadTeamMembers(userId);
    }
  }, [userId, loadTeamMembers]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      clearError && clearError('teamMembers');
    };
  }, [clearError]);

  const handleCreateTeamMember = useCallback(async (memberData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = createTeamMember ? await createTeamMember(memberData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [createTeamMember]);

  const handleUpdateTeamMember = useCallback(async (memberId, memberData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = updateTeamMember ? await updateTeamMember(memberId, memberData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [updateTeamMember]);

  const handleDeleteTeamMember = useCallback(async (memberId) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = deleteTeamMember ? await deleteTeamMember(memberId) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [deleteTeamMember]);

  const refreshTeamMembers = useCallback(() => {
    if (userId && loadTeamMembers) {
      return loadTeamMembers(userId);
    }
  }, [userId, loadTeamMembers]);

  // Get active team members
  const getActiveTeamMembers = useCallback(() => {
    return (teamMembers || []).filter(member => member.status === 'active');
  }, [teamMembers]);

  // Get team members by role
  const getTeamMembersByRole = useCallback((role) => {
    return (teamMembers || []).filter(member => member.role === role);
  }, [teamMembers]);

  // Get team performance average
  const getTeamPerformanceAverage = useCallback(() => {
    const activeMembers = getActiveTeamMembers();
    if (activeMembers.length === 0) return 0;
    
    const totalPerformance = activeMembers.reduce((sum, member) => sum + (member.performance || 0), 0);
    return Math.round(totalPerformance / activeMembers.length);
  }, [getActiveTeamMembers]);

  return {
    teamMembers: teamMembers || [],
    loading: (loading && loading.teamMembers) || localLoading,
    error: (errors && errors.teamMembers) || localError,
    createTeamMember: handleCreateTeamMember,
    updateTeamMember: handleUpdateTeamMember,
    deleteTeamMember: handleDeleteTeamMember,
    refreshTeamMembers,
    getActiveTeamMembers,
    getTeamMembersByRole,
    getTeamPerformanceAverage,
  };
}

/**
 * Hook for managing tasks
 */
export function useTasks(userId) {
  const {
    tasks,
    loading,
    errors,
    loadTasks,
    createTask,
    updateTask,
    updateTaskProgress,
    deleteTask,
    clearError,
  } = useDatabase();

  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Load tasks on mount
  useEffect(() => {
    if (userId) {
      loadTasks && loadTasks(userId);
    }
  }, [userId, loadTasks]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      clearError && clearError('tasks');
    };
  }, [clearError]);

  const handleCreateTask = useCallback(async (taskData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = createTask ? await createTask(taskData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [createTask]);

  const handleUpdateTask = useCallback(async (taskId, taskData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = updateTask ? await updateTask(taskId, taskData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [updateTask]);

  const handleUpdateProgress = useCallback(async (taskId, progress) => {
    try {
      const result = updateTaskProgress ? await updateTaskProgress(taskId, progress) : { success: false, error: 'Function not available' };
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [updateTaskProgress]);

  const handleDeleteTask = useCallback(async (taskId) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = deleteTask ? await deleteTask(taskId) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [deleteTask]);

  const refreshTasks = useCallback(() => {
    if (userId && loadTasks) {
      return loadTasks(userId);
    }
  }, [userId, loadTasks]);

  // Filter tasks by status
  const getTasksByStatus = useCallback((status) => {
    return (tasks || []).filter(task => task.status === status);
  }, [tasks]);

  // Get tasks by priority
  const getTasksByPriority = useCallback((priority) => {
    return (tasks || []).filter(task => task.priority === priority);
  }, [tasks]);

  // Get overdue tasks
  const getOverdueTasks = useCallback(() => {
    const now = new Date();
    return (tasks || []).filter(task => {
      if (!task.due_date || task.status === 'completed') return false;
      return new Date(task.due_date) < now;
    });
  }, [tasks]);

  return {
    tasks: tasks || [],
    loading: (loading && loading.tasks) || localLoading,
    error: (errors && errors.tasks) || localError,
    createTask: handleCreateTask,
    updateTask: handleUpdateTask,
    updateTaskProgress: handleUpdateProgress,
    deleteTask: handleDeleteTask,
    refreshTasks,
    getTasksByStatus,
    getTasksByPriority,
    getOverdueTasks,
  };
}

/**
 * Hook for managing time entries
 */
export function useTimeEntries(userId, date = null) {
  const {
    timeEntries,
    loading,
    errors,
    loadTimeEntries,
    createTimeEntry,
    updateTimeEntry,
    deleteTimeEntry,
    clearError,
  } = useDatabase();

  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Load time entries on mount
  useEffect(() => {
    if (userId) {
      loadTimeEntries && loadTimeEntries(userId, date);
    }
  }, [userId, date, loadTimeEntries]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      clearError && clearError('timeEntries');
    };
  }, [clearError]);

  const handleCreateTimeEntry = useCallback(async (timeData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = createTimeEntry ? await createTimeEntry(timeData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [createTimeEntry]);

  const handleUpdateTimeEntry = useCallback(async (entryId, timeData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = updateTimeEntry ? await updateTimeEntry(entryId, timeData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [updateTimeEntry]);

  const handleDeleteTimeEntry = useCallback(async (entryId) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = deleteTimeEntry ? await deleteTimeEntry(entryId) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [deleteTimeEntry]);

  const refreshTimeEntries = useCallback(() => {
    if (userId && loadTimeEntries) {
      return loadTimeEntries(userId, date);
    }
  }, [userId, date, loadTimeEntries]);

  // Calculate total hours
  const getTotalHours = useCallback(() => {
    return (timeEntries || []).reduce((total, entry) => total + (entry.duration || 0), 0);
  }, [timeEntries]);

  // Get entries by date range
  const getEntriesByDateRange = useCallback((startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    return (timeEntries || []).filter(entry => {
      const entryDate = new Date(entry.date);
      return entryDate >= start && entryDate <= end;
    });
  }, [timeEntries]);

  return {
    timeEntries: timeEntries || [],
    loading: (loading && loading.timeEntries) || localLoading,
    error: (errors && errors.timeEntries) || localError,
    createTimeEntry: handleCreateTimeEntry,
    updateTimeEntry: handleUpdateTimeEntry,
    deleteTimeEntry: handleDeleteTimeEntry,
    refreshTimeEntries,
    getTotalHours,
    getEntriesByDateRange,
  };
}

/**
 * Hook for managing notifications
 */
export function useNotifications(userId) {
  const {
    notifications,
    loading,
    errors,
    loadNotifications,
    createNotification,
    markNotificationAsRead,
    clearError,
  } = useDatabase();

  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Load notifications on mount
  useEffect(() => {
    if (userId) {
      loadNotifications && loadNotifications(userId);
    }
  }, [userId, loadNotifications]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      clearError && clearError('notifications');
    };
  }, [clearError]);

  const handleMarkAsRead = useCallback(async (notificationId) => {
    try {
      const result = markNotificationAsRead ? await markNotificationAsRead(notificationId) : { success: false, error: 'Function not available' };
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [markNotificationAsRead]);

  const handleCreateNotification = useCallback(async (notificationData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = createNotification ? await createNotification(notificationData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [createNotification]);

  const refreshNotifications = useCallback(() => {
    if (userId && loadNotifications) {
      return loadNotifications(userId);
    }
  }, [userId, loadNotifications]);

  // Get unread notifications
  const getUnreadNotifications = useCallback(() => {
    return (notifications || []).filter(notification => !notification.read);
  }, [notifications]);

  // Get notifications by type
  const getNotificationsByType = useCallback((type) => {
    return (notifications || []).filter(notification => notification.type === type);
  }, [notifications]);

  // Get unread count
  const getUnreadCount = useCallback(() => {
    return (notifications || []).filter(notification => !notification.read).length;
  }, [notifications]);

  return {
    notifications: notifications || [],
    loading: (loading && loading.notifications) || localLoading,
    error: (errors && errors.notifications) || localError,
    createNotification: handleCreateNotification,
    markAsRead: handleMarkAsRead,
    refreshNotifications,
    getUnreadNotifications,
    getNotificationsByType,
    getUnreadCount,
  };
}

/**
 * Hook for managing dashboard data
 */
export function useDashboard(userId) {
  const {
    dashboardStats,
    loading,
    errors,
    loadDashboardStats,
    refreshAllData,
    clearError,
  } = useDatabase();

  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Load dashboard data on mount
  useEffect(() => {
    if (userId) {
      loadDashboardStats && loadDashboardStats(userId);
    }
  }, [userId, loadDashboardStats]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      clearError && clearError('dashboard');
    };
  }, [clearError]);

  const refreshDashboard = useCallback(async () => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      if (userId && loadDashboardStats) {
        const result = await loadDashboardStats(userId);
        if (!result.success) {
          setLocalError(result.error);
        }
      }
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [userId, loadDashboardStats]);

  const refreshAllDashboardData = useCallback(async () => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      refreshAllData && await refreshAllData();
    } catch (error) {
      setLocalError(error.message);
    } finally {
      setLocalLoading(false);
    }
  }, [refreshAllData]);

  return {
    dashboardStats: dashboardStats || {},
    loading: (loading && loading.dashboard) || localLoading,
    error: (errors && errors.dashboard) || localError,
    refreshDashboard,
    refreshAllData: refreshAllDashboardData,
  };
}

/**
 * Hook for managing leave requests
 */
export function useLeaveRequests(userId) {
  const {
    leaveRequests,
    loading,
    errors,
    loadLeaveRequests,
    createLeaveRequest,
    updateLeaveRequest,
    clearError,
  } = useDatabase();

  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Load leave requests on mount
  useEffect(() => {
    if (userId) {
      loadLeaveRequests && loadLeaveRequests(userId);
    }
  }, [userId, loadLeaveRequests]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      clearError && clearError('leaveRequests');
    };
  }, [clearError]);

  const handleCreateLeaveRequest = useCallback(async (leaveData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = createLeaveRequest ? await createLeaveRequest(leaveData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [createLeaveRequest]);

  const handleUpdateLeaveRequest = useCallback(async (requestId, leaveData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = updateLeaveRequest ? await updateLeaveRequest(requestId, leaveData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [updateLeaveRequest]);

  const refreshLeaveRequests = useCallback(() => {
    if (userId && loadLeaveRequests) {
      return loadLeaveRequests(userId);
    }
  }, [userId, loadLeaveRequests]);

  // Get requests by status
  const getRequestsByStatus = useCallback((status) => {
    return (leaveRequests || []).filter(request => request.status === status);
  }, [leaveRequests]);

  // Get pending requests
  const getPendingRequests = useCallback(() => {
    return (leaveRequests || []).filter(request => request.status === 'pending');
  }, [leaveRequests]);

  return {
    leaveRequests: leaveRequests || [],
    loading: (loading && loading.leaveRequests) || localLoading,
    error: (errors && errors.leaveRequests) || localError,
    createLeaveRequest: handleCreateLeaveRequest,
    updateLeaveRequest: handleUpdateLeaveRequest,
    refreshLeaveRequests,
    getRequestsByStatus,
    getPendingRequests,
  };
}

/**
 * Hook for managing expenses
 */
export function useExpenses(userId) {
  const {
    expenses,
    loading,
    errors,
    loadExpenses,
    createExpense,
    updateExpense,
    deleteExpense,
    clearError,
  } = useDatabase();

  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Load expenses on mount
  useEffect(() => {
    if (userId) {
      loadExpenses && loadExpenses(userId);
    }
  }, [userId, loadExpenses]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      clearError && clearError('expenses');
    };
  }, [clearError]);

  const handleCreateExpense = useCallback(async (expenseData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = createExpense ? await createExpense(expenseData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [createExpense]);

  const handleUpdateExpense = useCallback(async (expenseId, expenseData) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = updateExpense ? await updateExpense(expenseId, expenseData) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [updateExpense]);

  const handleDeleteExpense = useCallback(async (expenseId) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const result = deleteExpense ? await deleteExpense(expenseId) : { success: false, error: 'Function not available' };
      if (!result.success) {
        setLocalError(result.error);
      }
      return result;
    } catch (error) {
      setLocalError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLocalLoading(false);
    }
  }, [deleteExpense]);

  const refreshExpenses = useCallback(() => {
    if (userId && loadExpenses) {
      return loadExpenses(userId);
    }
  }, [userId, loadExpenses]);

  // Get expenses by status
  const getExpensesByStatus = useCallback((status) => {
    return (expenses || []).filter(expense => expense.status === status);
  }, [expenses]);

  // Get total amount
  const getTotalAmount = useCallback(() => {
    return (expenses || []).reduce((total, expense) => total + (expense.amount || 0), 0);
  }, [expenses]);

  // Get expenses by date range
  const getExpensesByDateRange = useCallback((startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    return (expenses || []).filter(expense => {
      const expenseDate = new Date(expense.date);
      return expenseDate >= start && expenseDate <= end;
    });
  }, [expenses]);

  return {
    expenses: expenses || [],
    loading: (loading && loading.expenses) || localLoading,
    error: (errors && errors.expenses) || localError,
    createExpense: handleCreateExpense,
    updateExpense: handleUpdateExpense,
    deleteExpense: handleDeleteExpense,
    refreshExpenses,
    getExpensesByStatus,
    getTotalAmount,
    getExpensesByDateRange,
  };
}

/**
 * Hook for real-time data updates
 */
export function useRealTimeUpdates(userId, options = {}) {
  const { refreshAllData, getSyncStatus, performSync } = useDatabase();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const intervalRef = useRef(null);
  
  const {
    interval = 30000, // 30 seconds
    autoRefresh = true,
    syncOnMount = true,
  } = options;

  // Auto refresh data
  useEffect(() => {
    if (autoRefresh && userId) {
      intervalRef.current = setInterval(() => {
        refreshAllData && refreshAllData();
      }, interval);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [autoRefresh, userId, interval, refreshAllData]);

  // Sync on mount
  useEffect(() => {
    if (syncOnMount && userId) {
      performSync && performSync();
    }
  }, [syncOnMount, userId, performSync]);

  const manualRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      refreshAllData && await refreshAllData();
      performSync && await performSync();
    } catch (error) {
      console.error('Manual refresh failed:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshAllData, performSync]);

  const syncStatus = getSyncStatus ? getSyncStatus() : { status: 'unknown' };

  return {
    isRefreshing,
    manualRefresh,
    syncStatus,
  };
}

/**
 * Hook for offline support
 */
export function useOfflineSupport() {
  const { isOnline, isSyncing, syncStatus, performSync } = useDatabase();
  const [offlineActions, setOfflineActions] = useState([]);

  const addOfflineAction = useCallback((action) => {
    setOfflineActions(prev => [...prev, { ...action, timestamp: Date.now() }]);
  }, []);

  const clearOfflineActions = useCallback(() => {
    setOfflineActions([]);
  }, []);

  // Sync when coming back online
  useEffect(() => {
    if (isOnline && offlineActions.length > 0 && performSync) {
      performSync().then(() => {
        clearOfflineActions();
      });
    }
  }, [isOnline, offlineActions.length, performSync, clearOfflineActions]);

  return {
    isOnline: isOnline || true,
    isSyncing: isSyncing || false,
    syncStatus: syncStatus || { status: 'unknown' },
    offlineActions,
    addOfflineAction,
    clearOfflineActions,
    performSync: performSync || (() => Promise.resolve()),
  };
}


/**
 * Hook for loading the company logo from AsyncStorage (cache) and the remote API.
 *
 * Priority order:
 *   1. AsyncStorage  'company_profile'  (instant, offline-safe)
 *   2. Remote API    /api/data/company_profiles  (latest, requires network)
 *
 * Usage:
 *   const { logo, companyName, initials, loading, error, reload } = useCompanyLogo();
 *
 * Options:
 *   refreshOnFocus {boolean}  — reload automatically when screen comes into focus (default: true)
 *   apiBaseUrl     {string}   — override the default API base URL
 */
export function useCompanyLogo(refreshOnFocus = true, apiBaseUrl = 'https://api.jobeng.ai') {
  const [logo,        setLogo]        = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);

  // ── Derive up-to-2-character initials from the company name ──────────────
  const getInitials = useCallback((name = '') => {
    if (!name?.trim()) return '?';
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  }, []);

  // ── Core load function ────────────────────────────────────────────────────
  const loadLogo = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // 1. Serve from cache immediately so the UI has something to show
      const cachedRaw = await AsyncStorage.getItem('company_profile');
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        setLogo(cached.logo || null);
        setCompanyName(cached.name || cached.company_name || '');
      }

      // 2. Fetch fresh data from the server
      const [token, companyId] = await Promise.all([
        AsyncStorage.getItem('auth_token'),
        AsyncStorage.getItem('company_id'),
      ]);

      if (token && companyId) {
        const response = await fetch(
          `${apiBaseUrl}/api/data/company_profiles?company_id=${parseInt(companyId, 10)}`,
          {
            method: 'GET',
            headers: {
              'Content-Type':  'application/json',
              'Accept':        'application/json',
              'X-API-Key':     '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const json = await response.json();
          const profiles = json?.data;
          const profile  = Array.isArray(profiles) && profiles.length > 0
            ? profiles[0]
            : json?.data ?? null;

          if (profile) {
            const freshLogo = profile.logo || null;
            const freshName = profile.name || profile.company_name || '';

            setLogo(freshLogo);
            setCompanyName(freshName);

            // Merge into existing cached profile (don't blow away other fields)
            const merged = { ...(cachedRaw ? JSON.parse(cachedRaw) : {}), ...profile };
            await AsyncStorage.setItem('company_profile', JSON.stringify(merged));
          }
        }
        // If network fails, we already have the cache value — no action needed
      }
    } catch (err) {
      console.warn('[useCompanyLogo] Error loading logo:', err);
      setError(err);
      // Keep whatever logo we managed to set from cache — don't clear it
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl]);

  // ── Load on mount ─────────────────────────────────────────────────────────
  useEffect(() => {
    loadLogo();
  }, [loadLogo]);

  // ── Reload when screen comes into focus ───────────────────────────────────
  useFocusEffect(
    useCallback(() => {
      if (refreshOnFocus) {
        loadLogo();
      }
    }, [loadLogo, refreshOnFocus])
  );

  return {
    logo,
    companyName,
    initials: getInitials(companyName),
    loading,
    error,
    reload: loadLogo,
  };
}

export default {
  useSubscriptions,
  useCompanies,
  useUsers,
  useReports,
  useContracts,
  useProjects,
  useTeamMembers,
  useTasks,
  useTimeEntries,
  useNotifications,
  useDashboard,
  useLeaveRequests,
  useExpenses,
  useRealTimeUpdates,
  useOfflineSupport,
  useCompanyLogo,   // ← company logo loader (cache-first + API refresh)
};